import { useState, useEffect } from "react";
import { getCurrentSessionToken, getCurrentUserEmail } from "../services/cognito";
import type { AuthStep } from "./useLogin";

function parseHashParams(): Record<string, string> {
  const hash = window.location.hash.substring(1);
  const params: Record<string, string> = {};
  if (!hash) return params;
  
  hash.split("&").forEach((part) => {
    const temp = part.split("=");
    if (temp[0]) {
      params[temp[0]] = decodeURIComponent(temp[1] || "");
    }
  });
  return params;
}

function getUsernameFromIdToken(idToken: string): string {
  try {
    const base64Url = idToken.split(".")[1];
    if (!base64Url) throw new Error("Malformed JWT");
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const decoded = JSON.parse(jsonPayload);
    return decoded["cognito:username"] || decoded["sub"];
  } catch (e) {
    console.error("Error decoding ID token:", e);
    throw new Error("ID token inválido");
  }
}

async function exchangeCodeForTokens(code: string): Promise<{ id_token: string; access_token: string; refresh_token?: string }> {
  const domain = process.env.BUN_PUBLIC_COGNITO_DOMAIN;
  const clientId = process.env.BUN_PUBLIC_COGNITO_WEB_CLIENT_ID;
  const redirectUri = process.env.BUN_PUBLIC_COGNITO_REDIRECT_URI;

  if (!domain || !clientId || !redirectUri) {
    throw new Error("Falta configuración de entorno de Cognito (Domain, Client ID, Redirect URI)");
  }

  const response = await fetch(`${domain}/oauth2/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: clientId,
      code,
      redirect_uri: redirectUri,
    }),
  });

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Error en el intercambio de código: ${errText}`);
  }

  return response.json();
}

function saveTokensToLocalStorage(clientId: string, username: string, tokens: { id_token: string; access_token: string; refresh_token?: string }) {
  localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`, username);
  localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.idToken`, tokens.id_token);
  localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.accessToken`, tokens.access_token);
  if (tokens.refresh_token) {
    localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.refreshToken`, tokens.refresh_token);
  }
}

export function useAuthSession(
  setStep: (step: AuthStep) => void,
  setUserEmail: (email: string | null) => void,
  setError: (err: string) => void
) {
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const handleOAuthAndCheckSession = async () => {
      setIsInitializing(true);
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");
        const hashParams = parseHashParams();
        const clientId = process.env.BUN_PUBLIC_COGNITO_WEB_CLIENT_ID || "";

        let tokenExchangeSuccess = false;
        let idToken = hashParams.id_token;
        let accessToken = hashParams.access_token;
        let refreshToken = hashParams.refresh_token;

        if (code) {
          try {
            const tokens = await exchangeCodeForTokens(code);
            idToken = tokens.id_token;
            accessToken = tokens.access_token;
            refreshToken = tokens.refresh_token;
            tokenExchangeSuccess = true;
          } catch (err: any) {
            console.error("Error exchanging OAuth code:", err);
            setError(err?.message || "Error al procesar el inicio de sesión con Google.");
          }
        } else if (idToken && accessToken) {
          tokenExchangeSuccess = true;
        }

        if (tokenExchangeSuccess && idToken && accessToken) {
          const extractedUsername = getUsernameFromIdToken(idToken);
          saveTokensToLocalStorage(clientId, extractedUsername, {
            id_token: idToken,
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          // Clean URL params/hash without reloading the page
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }

        // Verify session via standard Cognito helper
        const token = await getCurrentSessionToken();
        if (token) {
          const email = await getCurrentUserEmail();
          setUserEmail(email);
          setStep("AUTHENTICATED");
        }
      } catch (e) {
        console.error("Session check or OAuth processing failed:", e);
      } finally {
        setIsInitializing(false);
      }
    };
    
    handleOAuthAndCheckSession();
  }, [setStep, setUserEmail, setError]);

  return { isInitializing };
}
